import { useMemo } from 'react';
import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';
import type { UseFeaturedPlacementsArgs, UseFeaturedPlacementsResult } from './useFeaturedPlacements.types';

/**
 * `row`: the band is the SECOND thing in the list — one organic result above it, always.
 *
 * Fixed, unlike the hero below: the band is a whole unit of several hotels with no single score to
 * rank it by, and its position is part of the treatment being tested. One organic result above it
 * means the page still opens on the guest's own sort rather than on an ad.
 */
export const FEATURED_BAND_INSERT_AFTER_INDEX = 1;

const isSponsored = (listing: AccommodationListingDTO) => listing.isSponsored === true;

/**
 * Where the hero sits: after every organic listing that outranks it on `searchScore`.
 *
 * The hero is NOT pinned to the top. It is promoted in presentation — a full-width unit instead of a
 * row — but it earns its place in the order like any other hotel, so a sponsored hotel the relevance
 * model ranks fifth appears fifth.
 *
 * Always `searchScore`, whatever sort the guest has picked. The alternative — using the listing's
 * place in the active sort — buries the placement under a price sort, because sponsored hotels carry
 * no price and sort last. Scoring it consistently keeps the position stable and predictable; it does
 * mean that under a price sort the hero sits at a position the surrounding prices do not explain.
 *
 * An unscored hero goes first rather than last: it cannot be ranked, and burying a paid placement on
 * missing data is a worse failure than showing it high.
 */
const scoredInsertIndex = (featured: AccommodationListingDTO, organicListings: ReadonlyArray<AccommodationListingDTO>): number => {
  const featuredScore = featured.searchScore;
  if (featuredScore === undefined) return 0;

  return organicListings.filter(listing => (listing.searchScore ?? -Infinity) > featuredScore).length;
};

/**
 * Missing score sorts last rather than as 0 — an unscored sponsored hotel is "unknown", not "worst".
 *
 * The equality branch is load-bearing: subtracting the resolved scores directly returns `NaN` when
 * both are `-Infinity` (two unscored listings), which breaks `Array.prototype.sort`'s strict-weak
 * ordering contract and makes the result implementation-defined.
 */
const byDescendingSearchScore = (a: AccommodationListingDTO, b: AccommodationListingDTO) => {
  const scoreA = a.searchScore ?? -Infinity;
  const scoreB = b.searchScore ?? -Infinity;
  if (scoreA === scoreB) return 0;
  return scoreB > scoreA ? 1 : -1;
};

/**
 * Splits the pinned paid placements out of the organic list for the featured-placement A/B test.
 *
 * The BE now honours the slot count the search asked for (`maxSponsoredSlots`), so this split is no
 * longer what decides how many sponsored hotels exist — it moves the ones that came back out of the
 * organic list and into the pinned unit, and it caps the result if the BE returned more than the
 * arm has room for.
 *
 * `control` is a pure passthrough. Note it is passthrough over an EMPTY sponsored set: control asks
 * for 0 slots, so there is nothing to split. It stays fail-closed for the case where a cached or
 * over-returning response contains sponsored hotels anyway — those render inline, as today.
 */
export const useFeaturedPlacements = ({ allListings, displayedListings, variant }: UseFeaturedPlacementsArgs): UseFeaturedPlacementsResult => {
  return useMemo<UseFeaturedPlacementsResult>(() => {
    if (variant === 'control') {
      return { featuredListings: [], organicListings: displayedListings, insertAfterIndex: 0 };
    }

    const sponsored = allListings.filter(isSponsored);

    if (sponsored.length === 0) {
      return { featuredListings: [], organicListings: displayedListings, insertAfterIndex: 0 };
    }

    /**
     * WHO CHOOSES THE PAID SLOTS: the BE does. The search sends `maxSponsoredSlots` (control 0,
     * hero 1, row 4) and the server returns the properties that fill them, capped by the contracted
     * slot count. Selection is no longer a client decision.
     *
     * `searchScore` therefore no longer stands in for a commercial rank as the primary mechanism —
     * it is a DEFENSIVE FALLBACK for the case where more sponsored properties come back than the
     * arm has room for (an over-returning BE, a stale cached response, or a couple's-pick listing
     * merged in from the other query, none of which respect the slot count). When that happens we
     * still have to pick, and relevance is the only ordering signal on the payload: `isSponsored` is
     * a bare boolean with no campaign id, bid or slot index beside it.
     *
     * The caveat that survives: in that fallback path the ordering is still relevance, not the
     * advertiser's. It stays acceptable only because it is an edge case — if this ever becomes the
     * routine path again, or the test starts informing pricing or slot allocation, it needs a
     * BE-provided rank/slot field instead.
     */
    const rankedSponsored = [...sponsored].sort(byDescendingSearchScore);

    // Both treatments take sponsored hotels out of the sorted list. Under `hero` the ones that did
    // not win the slot are suppressed from the page, not shown inline — so the same filter serves
    // both, and only `featuredListings` differs.
    const organicListings = displayedListings.filter(listing => !isSponsored(listing));

    if (variant === 'hero') {
      const hero = rankedSponsored[0];
      return { featuredListings: [hero], organicListings, insertAfterIndex: scoredInsertIndex(hero, organicListings) };
    }

    // `row`: every sponsored hotel the BE returned, no cap.
    return { featuredListings: rankedSponsored, organicListings, insertAfterIndex: FEATURED_BAND_INSERT_AFTER_INDEX };
  }, [allListings, displayedListings, variant]);
};
